import { DeleteCellRenderer } from '@/components/grid';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { useMutation, useApolloClient, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import type { ColDef, ValueGetterParams } from 'ag-grid-community';
import {
	useState,
	useEffect,
	useMemo,
	useCallback,
	forwardRef,
	useImperativeHandle
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LimitDetails } from './limit-details';
import { LIMITS_LIST_QUERY, LIMIT_DELETE_MUTATION } from './limits.gql';
import {
	selectLimitAfterSave,
	type LimitListRecord
} from './limit-list-selection';
import { cloneLimit } from './cloneLimit';
import { Gauge } from 'lucide-react';

interface Limit {
	limitId: string;
	name: string;
	minAmount?: number;
	maxAmount?: number;
	minStayMinutes?: number;
	maxStayMinutes?: number;
}

interface LimitsListResponse {
	limits?: {
		nodes?: Limit[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

export interface LimitsListRef {
	selectLimitAfterSave: (limitId: string) => Promise<void>;
}

const LimitEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Gauge className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const LimitsList = forwardRef<LimitsListRef>((_, ref) => {
	const { t } = useTranslation('limits');
	const [selectedLimit, setSelectedLimit] = useState<Limit | null>(null);
	const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
	const [localLimits, setLocalLimits] = useState<LimitListRecord[]>([]);
	const { showDialog } = useConfirmationDialog();
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();
	const { goToCreate, goToEdit } = useResourceNavigation('limits');
	const go = useGo();

	const [deleteLimit, { loading: deleteLoading }] = useMutation(
		LIMIT_DELETE_MUTATION
	);

	// Track total items to fetch (for preserving count after create/edit refetch)
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);

	const {
		data,
		loading: isLoading,
		refetch,
		fetchMore
	} = useQuery<LimitsListResponse>(LIMITS_LIST_QUERY, {
		variables: {
			first: itemsToFetch
		}
	});

	// Pagination state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.limits?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.limits?.pageInfo?.endCursor;

	// Load more handler using fetchMore
	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.limits) return prev;

					return {
						...prev,
						limits: {
							...fetchMoreResult.limits,
							nodes: [
								...(prev.limits?.nodes ?? []),
								...(fetchMoreResult.limits.nodes ?? [])
							]
						}
					};
				}
			});
			// Reset local limits to use server data after fetchMore
			setLocalLimits([]);
		} catch (error) {
			console.error('Error loading more limits:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor]);

	const limitsData = data?.limits?.nodes ?? [];

	const handleRefresh = () => {
		setLocalLimits([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		refetch({ first: DEFAULT_PAGE_SIZE });
	};

	// Use local limits data if available, otherwise fall back to server data
	const limits = useMemo(() => {
		const serverLimits = limitsData || [];
		// Always prefer server data - local state is only for optimistic updates
		if (localLimits.length === 0 || serverLimits.length === 0) {
			return serverLimits;
		}
		return localLimits;
	}, [limitsData, localLimits]);

	// Expose method to select limit after save via ref
	useImperativeHandle(
		ref,
		() => ({
			selectLimitAfterSave: async (limitId: string) => {
				await selectLimitAfterSave({
					limitId,
					client: apolloClient,
					currentLimits: limits,
					onDataUpdate: setLocalLimits,
					onLimitSelect: setSelectedLimit,
					onOpenDetailsPanel: () => setDetailsPanelOpen(true)
				});
			}
		}),
		[apolloClient, limits]
	);

	// Handle limit selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectLimitId?: string } | null;
		if (state?.selectLimitId && limits.length > 0 && !isLoading) {
			const limitIdToSelect = state.selectLimitId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectLimitAfterSave({
						limitId: limitIdToSelect,
						client: apolloClient,
						currentLimits: limits,
						onDataUpdate: setLocalLimits,
						onLimitSelect: setSelectedLimit,
						onOpenDetailsPanel: () => setDetailsPanelOpen(true)
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select limit after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		limits.length,
		isLoading,
		apolloClient,
		limits,
		navigate,
		location.pathname
	]);

	const handleRowClick = (limit: Limit) => {
		setSelectedLimit(limit);
		setDetailsPanelOpen(true);
	};

	const handleCloseDetails = () => {
		setDetailsPanelOpen(false);
		setSelectedLimit(null);
	};

	// Helper function to find the next limit to select after deletion
	const findNextLimitToSelect = (deletedLimit: Limit, limitList: Limit[]) => {
		const currentIndex = limitList.findIndex(
			l => l.limitId === deletedLimit.limitId
		);

		if (limitList.length <= 1) {
			return null;
		}

		// Try to select the next limit (same index after deletion)
		if (currentIndex < limitList.length - 1) {
			return limitList[currentIndex + 1];
		}

		// If deleting the last limit, select the previous one
		if (currentIndex > 0) {
			return limitList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextLimitToSelect: Limit | null,
		updatedLimits: Limit[]
	) => {
		// First, clear the current selection completely
		setLocalLimits([]);
		setSelectedLimit(null);
		setDetailsPanelOpen(false);

		// Then after a brief delay, set the next limit
		setTimeout(() => {
			if (nextLimitToSelect && updatedLimits.length > 0) {
				// Find the limit in the updated list by ID
				const updatedLimit = updatedLimits.find(
					l => l.limitId === nextLimitToSelect?.limitId
				);

				if (updatedLimit) {
					setSelectedLimit(updatedLimit);
					setDetailsPanelOpen(true);
				}
			}
		}, 100);
	};

	const handleDeleteLimit = async (limit: Limit) => {
		const confirmed = await showDialog(
			'Delete Limit',
			`Are you sure you want to delete limit "${limit.name}"? This action cannot be undone.`
		);

		if (confirmed) {
			// Determine if we need to find the next limit to select
			const wasSelectedLimit = selectedLimit?.limitId === limit.limitId;

			// Clear selection immediately if deleting the selected limit
			if (wasSelectedLimit) {
				setSelectedLimit(null);
				setDetailsPanelOpen(false);
			}

			try {
				const result = await deleteLimit({
					variables: {
						input: {
							limitId: limit.limitId
						}
					},
					refetchQueries: ['Limits']
				});

				const checkResult = result.data?.deleteLimit;

				if (checkResult?.success) {
					showMutationSuccessToast('limits', 'delete');

					// Find the next limit to select after deletion (before refetch)
					let nextLimitToSelect = null;
					if (wasSelectedLimit) {
						nextLimitToSelect = findNextLimitToSelect(
							limit,
							limits || []
						);
					}

					// Refetch data
					refetch().then(result => {
						const updatedLimits = result.data?.limits?.nodes ?? [];
						handlePostDeletionSelection(
							nextLimitToSelect,
							updatedLimits
						);
					});
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(`Failed to delete limit: ${errorMessage}`);
				}
			} catch (error) {
				console.error('Delete limit error:', error);
				toast.error('Failed to delete limit. Please try again.');
			}
		}
	};
	const handleDeleteLimitById = async (limitId: string) => {
		const limit = limits?.find(l => l.limitId === limitId);
		if (limit) {
			await handleDeleteLimit(limit);
		}
	};

	const handleCloneLimit = (limitId: string) => {
		const limit = limits?.find(l => l.limitId === limitId);
		if (limit) {
			const cloneData = cloneLimit({
				name: limit.name,
				minAmount: limit.minAmount,
				maxAmount: limit.maxAmount,
				minStayMinutes: limit.minStayMinutes,
				maxStayMinutes: limit.maxStayMinutes
			});
			go({
				to: '/limits/create',
				state: { cloneData }
			});
		}
	};

	// Define AG Grid columns
	const columns: ColDef<Limit>[] = [
		{
			headerName: t('list.columns.name'),
			field: 'name',
			flex: 1,
			minWidth: 150
		},
		{
			headerName: t('list.columns.amountCap'),
			colId: 'amountCap',
			cellClass: 'ag-right-aligned-cell',
			headerClass: 'ag-right-aligned-header',
			valueGetter: (params: ValueGetterParams<Limit>) => {
				const minAmount = params.data?.minAmount;
				const maxAmount = params.data?.maxAmount;

				if (!minAmount && !maxAmount) return 'No cap';
				if (minAmount && maxAmount)
					return `${minAmount} - ${maxAmount}`;
				if (minAmount) return `Min ${minAmount}`;
				if (maxAmount) return `Max ${maxAmount}`;
				return 'No cap';
			}
		},
		{
			headerName: t('list.columns.timeCap'),
			colId: 'timeCap',
			valueGetter: (params: ValueGetterParams<Limit>) => {
				const minStay = params.data?.minStayMinutes;
				const maxStay = params.data?.maxStayMinutes;

				if (!minStay && !maxStay) return 'No cap';

				const formatMinutes = (minutes: number) => {
					const hours = Math.floor(minutes / 60);
					const mins = minutes % 60;
					if (hours > 0) {
						return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
					}
					return `${mins}m`;
				};

				if (minStay && maxStay)
					return `${formatMinutes(minStay)} - ${formatMinutes(maxStay)}`;
				if (minStay) return `Min ${formatMinutes(minStay)}`;
				if (maxStay) return `Max ${formatMinutes(maxStay)}`;
				return 'No cap';
			}
		},
		{
			headerName: '',
			field: undefined,
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			cellRenderer: DeleteCellRenderer,
			cellRendererParams: {
				onDelete: handleDeleteLimit
			},
			sortable: false,
			filter: false,
			resizable: false
		}
	];

	return (
		<ListPageLayout
			columns={columns}
			data={limits || []}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={isLoading || deleteLoading}
			onRowClick={handleRowClick}
			onCreate={goToCreate}
			onRefresh={handleRefresh}
			detailsPanelOpen={detailsPanelOpen}
			onCloseDetails={handleCloseDetails}
			selectedItem={selectedLimit}
			detailsTitle="Limit Details"
			onEdit={goToEdit}
			onDelete={handleDeleteLimitById}
			onClone={handleCloneLimit}
			onOpenInNewWindow={id => openEntityInNewWindow('limits', id)}
			renderDetailsContent={(limit: Limit) => (
				<LimitDetails limitId={limit.limitId} />
			)}
			getRecordId={limit => limit.limitId}
			getDetailsDescription={limit => limit.name}
			renderEntityIcon={() => <LimitEntityIcon />}
			getEntityTypeName={() => t('details.limit')}
			showCreateButton={true}
			showRefreshButton={true}
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
});

LimitsList.displayName = 'LimitsList';
